import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { StatusBar, StatChips, damageTone } from '../components/metrics';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Building2, Plus, ArrowRight, Loader2, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';

export default function SocietyMaster() {
  const { user } = useAuth();
  const canCreate = ['admin', 'manager'].includes(user.role);
  const [list, setList] = useState(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', city: '', address: '' });
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/societies/master').then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return toast.error('Name required');
    setSaving(true);
    try { await api.post('/societies', form); toast.success('Society created'); setOpen(false); setForm({ name: '', city: '', address: '' }); load(); }
    catch { toast.error('Failed'); }
    setSaving(false);
  };

  const filtered = (list || []).filter((s) => s.name.toLowerCase().includes(q.toLowerCase()) || (s.city || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><p className="overline text-primary">Society Master</p><h1 className="font-head text-3xl font-bold">Societies</h1></div>
        <div className="flex items-center gap-2">
          <Input placeholder="Search societies…" data-testid="society-search" value={q} onChange={(e) => setQ(e.target.value)} className="w-48" />
          {canCreate && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid="add-society-master-btn"><Plus className="mr-1 h-4 w-4" /> New Society</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-head text-2xl">New Society</DialogTitle></DialogHeader>
                <div className="space-y-2">
                  <Input placeholder="Society name" data-testid="ms-society-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                  <Input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </div>
                <DialogFooter><Button onClick={create} disabled={saving} data-testid="ms-save-society">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {!list ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((s) => (
            <Link key={s.id} to={`/societies/${s.id}`} data-testid={`society-master-card-${s.id}`}
              className="tile-press rounded-xl border-2 border-border bg-card p-5 hover:border-primary">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/15 text-primary"><Building2 className="h-6 w-6" /></div>
                  <div>
                    <p className="font-head text-xl font-bold leading-tight">{s.name}</p>
                    <p className="text-xs text-muted-foreground">{s.city || '—'} · {s.tower_count} towers · {s.lift_count} lifts</p>
                  </div>
                </div>
                <span className={`flex items-center gap-1 font-head text-lg font-bold ${damageTone(s.stats.damage_pct)}`}>
                  {s.stats.damage_pct >= 8 && <TriangleAlert className="h-4 w-4" />}{s.stats.damage_pct}%
                </span>
              </div>
              <div className="mt-4">
                <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{s.stats.total} sites · {s.stats.install_pct}% installed</span>
                  {s.billing_status && <span className="rounded-full border border-border px-2 py-0.5 uppercase">{s.billing_status}</span>}
                </div>
                <StatusBar counts={s.stats.status_counts} total={s.stats.total} />
              </div>
              <div className="mt-3 flex items-center justify-end text-xs text-primary">Open society <ArrowRight className="ml-1 h-3.5 w-3.5" /></div>
            </Link>
          ))}
          {!filtered.length && <p className="text-sm text-muted-foreground">No societies found.</p>}
        </div>
      )}
    </div>
  );
}
